import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { Typography, TypographyProps } from '@mui/material';

interface SafeHtmlRendererProps extends Omit<TypographyProps, 'children' | 'dangerouslySetInnerHTML'> {
  htmlContent: string;
  fallbackText?: string;
  maxLength?: number;
  showEllipsis?: boolean;
}

const SafeHtmlRenderer: React.FC<SafeHtmlRendererProps> = ({
  htmlContent,
  fallbackText = '',
  maxLength,
  showEllipsis = false,
  ...typographyProps
}) => {
  const sanitizedHtml = useMemo(() => {
    if (!htmlContent) return fallbackText;

    // Configure DOMPurify to allow safe HTML elements and attributes
    const config = {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'a', 'span', 'div'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
      ALLOW_DATA_ATTR: false,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_DOM_IMPORT: false,
    };

    const cleanHtml = DOMPurify.sanitize(htmlContent, config);

    // If maxLength is specified, truncate the text content
    if (maxLength && maxLength > 0) {
      // Create a temporary element to extract text content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = cleanHtml;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      
      if (textContent.length > maxLength) {
        // Truncate text and show ellipsis if needed
        const truncatedText = textContent.substring(0, maxLength);
        const lastSpace = truncatedText.lastIndexOf(' ');
        const finalText = lastSpace > 0 ? truncatedText.substring(0, lastSpace) : truncatedText;
        
        return showEllipsis ? `${finalText}...` : finalText;
      }
    }

    return cleanHtml;
  }, [htmlContent, fallbackText, maxLength, showEllipsis]);

  // If content is plain text (no HTML tags), render as regular text
  const isPlainText = !sanitizedHtml.includes('<');

  if (isPlainText) {
    return (
      <Typography {...typographyProps}>
        {sanitizedHtml}
      </Typography>
    );
  }

  // Render HTML content safely
  return (
    <Typography
      {...typographyProps}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      sx={{
        // Ensure proper styling for HTML elements
        '& p': {
          margin: '0 0 8px 0',
          '&:last-child': {
            marginBottom: 0,
          },
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          margin: '16px 0 8px 0',
          fontWeight: 'bold',
          '&:first-child': {
            marginTop: 0,
          },
        },
        '& h1': { fontSize: '1.5rem' },
        '& h2': { fontSize: '1.25rem' },
        '& h3': { fontSize: '1.1rem' },
        '& ul, & ol': {
          margin: '8px 0',
          paddingLeft: '20px',
        },
        '& li': {
          margin: '4px 0',
        },
        '& a': {
          color: 'primary.main',
          textDecoration: 'underline',
          '&:hover': {
            textDecoration: 'none',
          },
        },
        '& strong': {
          fontWeight: 'bold',
        },
        '& em': {
          fontStyle: 'italic',
        },
        '& u': {
          textDecoration: 'underline',
        },
        ...typographyProps.sx,
      }}
    />
  );
};

export default SafeHtmlRenderer;